<script setup lang="ts">
import { Pencil, Trash2 } from 'lucide';
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import { format, isValid, parseISO } from 'date-fns';
import EventBus from '@legacy/js/service/EventBus';
import type { Notify } from '@/composables/notifications';
import { SphButton } from '@/components/base/button';
import { SphDatePicker } from '@/components/base/date-picker';
import { SphSelect } from '@/components/base/select';
import axios from 'axios';
import { useI18n } from '@/composables/i18n';

defineOptions({
    compatConfig: { MODE: 3 },
})

const props = defineProps<{
    federationId: number
}>()

const { i18n } = useI18n()
const notify = inject<Notify>('notify')!

interface Section { id: number; name: string }
interface Kindergarten { id: number; name: string }
interface Relation {
    id: number
    section: Section
    start: Date
    end: Date | null
}
interface EditRelation {
    id: number
    start: Date
    end: Date | null
}

const kindergartens = ref<Kindergarten[]>([])
const sections = ref<Section[]>([])
const relations = ref<Relation[]>([])
const selectedKindergartenId = ref<string | undefined>(undefined)
const newSectionId = ref<string | undefined>(undefined)
const newStart = ref<Date | null>(null)
const newEnd = ref<Date | null>(null)
const editRelations = ref<EditRelation[]>([])
const pendingCreate = ref(false)
const pendingUpdate = ref(false)

const kindergartenOptions = computed(() => kindergartens.value.map(k => ({ value: String(k.id), label: k.name })))
const sectionOptions = computed(() => sections.value.map(s => ({ value: String(s.id), label: s.name })))

const selectedKindergarten = computed(() => kindergartens.value.find(k => String(k.id) === selectedKindergartenId.value) ?? null)

const loadingRelations = ref(false)
const loadRelations = () => {
    if (!selectedKindergarten.value || loadingRelations.value) return

    loadingRelations.value = true
    axios.get(`/kindergartens/${selectedKindergarten.value.id}/section-relations`)
        .then(r => {
            relations.value = r.data.data.map((d: any) => ({
                id: d.id,
                section: { id: d.section.data.id, name: d.section.data.name },
                start: parseISO(d.start),
                end: d.end ? parseISO(d.end) : null,
            }))
        })
        .catch(() => notify.error('error.loadingData'))
        .finally(() => { loadingRelations.value = false })
}

const loadSections = () => {
    axios.get(`/federations/${props.federationId}/sections`)
        .then(r => { sections.value = r.data.data.map((d: any) => ({ id: d.id, name: d.name })) })
        .catch(() => notify.error('error.loadingData'))
}

let unsubscribeSection: (() => void) | undefined

onMounted(() => {
    axios.get(`/federations/${props.federationId}/kindergartens`)
        .then(r => { kindergartens.value = r.data.data.map((d: any) => ({ id: d.id, name: d.name })) })
        .catch(() => notify.error('error.loadingData'))
    loadSections()
    unsubscribeSection = EventBus.$on('section:changed', (federationId: number) => {
        if (federationId === props.federationId) {
            loadSections()
            loadRelations()
        }
    })
})

onUnmounted(() => {
    unsubscribeSection?.()
})

watch(selectedKindergartenId, () => {
    editRelations.value = []
    newSectionId.value = undefined
    newStart.value = null
    newEnd.value = null
    loadRelations()
})

const isEditingRelation = (relation: Relation) => editRelations.value.some(e => e.id === relation.id)
const getEditRelation = (relation: Relation) => editRelations.value.find(e => e.id === relation.id)!

const startEdit = (relation: Relation) => {
    if (!isEditingRelation(relation)) {
        editRelations.value.push({ id: relation.id, start: relation.start, end: relation.end })
    }
}

const isValidRange = (start: Date | null, end: Date | null) => {
    if (!end) return true
    return start && isValid(start) && isValid(end) && end >= start
}

const newIsValid = computed(() =>
    newSectionId.value &&
    newStart.value &&
    isValid(newStart.value) &&
    isValidRange(newStart.value, newEnd.value)
)

const isSavable = computed(() => {
    if (!newSectionId.value && editRelations.value.length === 0) return false
    if (newSectionId.value && !newIsValid.value) return false
    if (editRelations.value.some(e => !isValidRange(e.start, e.end))) return false
    return true
})

const create = () => {
    if (!selectedKindergarten.value || !newSectionId.value || !newStart.value) return
    pendingCreate.value = true
    axios.post('/section-kindergarten-relations', {
        kindergartenId: selectedKindergarten.value.id,
        sectionId: Number(newSectionId.value),
        start: format(newStart.value, 'yyyy-MM-dd'),
        end: newEnd.value ? format(newEnd.value, 'yyyy-MM-dd') : null,
    })
        .then(() => {
            newSectionId.value = undefined
            newStart.value = null
            newEnd.value = null
            notify.success('saveChanges.success')
            loadRelations()
        })
        .catch((error: any) => {
            if (error.response?.status === 409) {
                const name = sections.value.find(s => String(s.id) === newSectionId.value)?.name ?? ''
                notify.error('section.relation.saveChanges.conflict', { name })
            } else {
                notify.error('saveChanges.error')
            }
        })
        .finally(() => { pendingCreate.value = false })
}

const update = () => {
    pendingUpdate.value = true
    Promise.all(editRelations.value.map(e =>
        axios.put(`/section-kindergarten-relations/${e.id}`, {
            start: e.start ? format(e.start, 'yyyy-MM-dd') : '',
            end: e.end ? format(e.end, 'yyyy-MM-dd') : null,
        })
    ))
        .then(() => {
            editRelations.value = []
            notify.success('saveChanges.success')
            loadRelations()
        })
        .catch(() => notify.error('saveChanges.error'))
        .finally(() => { pendingUpdate.value = false })
}

const save = () => {
    if (newSectionId.value && newIsValid.value) create()
    if (editRelations.value.length > 0) update()
}

const remove = (relation: Relation) => {
    ;(window as any).showConfirm(i18n.t('confirm'), i18n.t('section.relation.confirm.delete', { name: relation.section.name }), () => {
        editRelations.value = editRelations.value.filter(e => e.id !== relation.id)
        axios.delete(`/section-kindergarten-relations/${relation.id}`)
            .then(() => {
                relations.value = relations.value.filter(r => r.id !== relation.id)
                notify.success('section.relation.delete.success', { name: relation.section.name })
            })
            .catch(() => notify.error('saveChanges.error'))
    }, 'Ok')
}
</script>

<template>
    <div>
        <SphSelect
            v-model="selectedKindergartenId"
            :options="kindergartenOptions"
            class="kgr-row"
        />
        <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row text-foreground">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline w-full">
                        {{ i18n.t('section') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline min-w-[10rem]">
                        {{ i18n.t('Start') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline min-w-[10rem]">
                        {{ i18n.t('Ende') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody v-if="selectedKindergarten && !loadingRelations">
                <tr class="kgr-table__row">
                    <td class="kgr-table__cell">
                        <SphSelect
                            v-model="newSectionId"
                            :options="sectionOptions"
                            :placeholder="i18n.t('section')"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <SphDatePicker
                            v-model="newStart"
                            :invalid="!!newSectionId && !newStart"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <SphDatePicker
                            v-model="newEnd"
                            :invalid="!isValidRange(newStart, newEnd)"
                        />
                    </td>
                    <td class="kgr-table__cell"></td>
                </tr>
                <tr
                    v-for="relation in relations"
                    :key="relation.id"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ relation.section.name }}
                    </td>
                    <td class="kgr-table__cell">
                        <SphDatePicker
                            v-if="isEditingRelation(relation)"
                            v-model="getEditRelation(relation).start"
                            :invalid="!isValidRange(getEditRelation(relation).start, getEditRelation(relation).end)"
                        />
                        <template v-else>
                            {{ isValid(relation.start) ? format(relation.start, 'dd.MM.yyyy') : '' }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <SphDatePicker
                            v-if="isEditingRelation(relation)"
                            v-model="getEditRelation(relation).end"
                            :invalid="!isValidRange(getEditRelation(relation).start, getEditRelation(relation).end)"
                        />
                        <template v-else>
                            {{ relation.end && isValid(relation.end) ? format(relation.end, 'dd.MM.yyyy') : '' }}
                        </template>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <SphButton
                            :icon="Pencil"
                            variant="transparent"
                            size="sm"
                            :disabled="isEditingRelation(relation)"
                            class="kgr-table__action"
                            @click="startEdit(relation)"
                        />
                        <SphButton
                            :icon="Trash2"
                            variant="transparent"
                            size="sm"
                            class="kgr-table__action"
                            @click="remove(relation)"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
        <div
            v-if="selectedKindergarten && !loadingRelations"
            class="kgr-text kgr-text--rightAligned"
        >
            <SphButton
                :label="i18n.t('saveChanges')"
                :disabled="!isSavable"
                :loading="pendingCreate || pendingUpdate"
                @click="save"
            />
        </div>
    </div>
</template>
